import { z } from 'zod';
import { compiledPackageRefSchema } from '../macroPackage/packageRun';
import { definitionPinSchema } from './library';
import { graphCorrelationIdSchema, graphCountSchema, graphIdSchema } from './primitives';
import { externalCallResultSchema, nodeContextSchema } from './rules';
import { macroGraphDocumentSchema } from './source';
import { graphTestIdentitySchema, graphTestTargetSchema } from './diagnosticIdentity';

export const graphTestScenarioSchema = z.object({ id: graphIdSchema, name: z.string().min(1).max(120),
  clock: z.object({ startUnixMs: graphCountSchema, seed: z.string().min(1) }).strict(),
  environments: z.array(z.object({ atUs: graphCountSchema, nodeId: graphIdSchema,
    environment: nodeContextSchema.innerType().shape.environment }).strict()),
  attachments: z.array(z.object({ nodeId: graphIdSchema, attachmentId: graphCorrelationIdSchema, callId: graphCorrelationIdSchema,
    receipt: externalCallResultSchema.omit({ activationId: true }) }).strict()),
  matches: z.array(z.object({ atUs: graphCountSchema, definition: definitionPinSchema, stepId: graphIdSchema, matched: z.boolean() }).strict()),
  inputResults: z.array(z.object({ atUs: graphCountSchema, edgeId: graphIdSchema, stepId: graphIdSchema,
    outcome: z.enum(['succeeded', 'failed']) }).strict()) }).strict().superRefine((scenario, context) => {
    if (scenario.attachments.some(fixture => fixture.attachmentId !== fixture.receipt.attachmentId || fixture.callId !== fixture.receipt.callId)) context.addIssue({
      code: z.ZodIssueCode.custom, message: 'Scenario receipt must bind its declared attachment and call' });
  });
export type GraphTestScenario = z.infer<typeof graphTestScenarioSchema>;
export const graphTestStartSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('observe-live'), identity: graphTestIdentitySchema, artifact: compiledPackageRefSchema,
    scope: graphTestTargetSchema, scenario: graphTestScenarioSchema.nullable() }).strict(),
  z.object({ kind: z.literal('simulate'), identity: graphTestIdentitySchema, source: macroGraphDocumentSchema,
    scenario: graphTestScenarioSchema }).strict(),
]).superRefine((request, context) => {
  const identity = request.identity;
  if (request.kind === 'observe-live') {
    if (identity.executionDigest === null || identity.graphSha256 === null || identity.targetSessionId === null || identity.hostEpoch === null) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'Live observation requires the exact compiled artifact and interactive Host identity' });
    }
  } else {
    const source = request.source;
    if (source.id !== identity.graphId || request.scenario.environments.some(fixture => !source.nodes.some(node => node.id === fixture.nodeId))
      || request.scenario.attachments.some(fixture => !source.nodes.some(node => node.id === fixture.nodeId
        && node.powershellAttachments.some(attachment => attachment.attachmentId === fixture.attachmentId)))
      || request.scenario.matches.some(fixture => !source.definitions.some(definition => definition.id === fixture.definition.definitionId
        && definition.steps.some(step => step.id === fixture.stepId && step.kind === 'match-input')))
      || request.scenario.inputResults.some(fixture => !source.edges.some(edge => edge.id === fixture.edgeId
        && source.definitions.some(definition => definition.id === edge.definitionId
          && definition.steps.some(step => step.id === fixture.stepId && step.kind === 'match-input'))))) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'Scenario targets must belong to the supplied graph source' });
    }
  }
  if (request.scenario && identity.targetSessionId !== null && request.scenario.environments.some(fixture => fixture.environment.targetSessionId !== identity.targetSessionId)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Scenario environment must belong to the selected interactive target' });
  }
});
export type GraphTestStart = z.infer<typeof graphTestStartSchema>;
